/*
 * Routines client tracking. Loaded on every lab-layout page.
 *
 * Four jobs:
 *   1. ATTRIBUTION STOPGAP. Article-body /go/ links carry no ?a=<handle>, so ~20%
 *      of affiliate clicks land in Analytics Engine as article="direct". Only
 *      lab.stack items get ?a= (via snippets/aff.liquid). The worker's Referer
 *      fallback cannot help: browsers send strict-origin-when-cross-origin, so
 *      the path is stripped. This stamps ?a= on any go link missing it.
 *      Permanent fix is affiliate/tag_body_links.py rewriting the stored bodies.
 *   2. affiliate_click  - outbound affiliate clicks, with page context.
 *   3. ems_promo_click  - the EMS promo card and footer band, previously untracked,
 *      so there was no way to tell whether the site-wide push worked.
 *   4. read_depth       - 25/50/75/90% sentinels. Mobile fires the built-in 90%
 *      scroll event on 6.9% of pageviews vs 16.6% on desktop; the single 90% event
 *      cannot say WHERE readers leave, which is what we need to fix it.
 *
 * Note GA4 structurally cannot see go.routines.club clicks on its own:
 * app.routines.club and go.routines.club share a registrable domain, so enhanced
 * measurement never classifies them as outbound. Cloudflare Analytics Engine stays
 * the system of record for click counts; these events add the session, device and
 * landing-page context that AE has no way to know.
 *
 * NEVER append params to Amazon Attribution (maas) URLs. Doing so silently broke
 * purchase attribution once already. See GOALS.md hard rule 5.
 */
(function () {
  "use strict";

  var meta = window.__routinesMeta || {};
  var MAAS = /[?&]maas=|tag=maas|ref_=aa_maas/;
  var AFFILIATE = /(go\.routines\.club\/|routines\.club\/go\/|lvnta\.com|amazon\.|amzn\.to|a\.co\/|\.sjv\.io|\.pxf\.io|jdoqocy\.com|kqzyfj\.com|tkqlhce\.com|anrdoezrs\.net|geni\.us|visionbody)/i;

  // article.handle is blog-prefixed on this store ("routines/warren-buffetts-daily-routine").
  // Analytics Engine records the bare handle in blob2, so strip the prefix here or the
  // two datasets cannot be joined.
  var HANDLE = (meta.handle || "").split("/").pop();

  function ctx() {
    return {
      page_location: meta.url || location.href,
      page_title: meta.title || document.title,
      article_handle: HANDLE,
      article_category: meta.category || ""
    };
  }

  function send(name, params) {
    if (typeof window.gtag !== "function") return;
    var p = ctx();
    for (var k in params) { if (params.hasOwnProperty(k)) p[k] = params[k]; }
    p.transport_type = "beacon";
    window.gtag("event", name, p);
  }

  function hostOf(u) {
    try { return new URL(u, location.href).hostname; } catch (e) { return ""; }
  }

  function slugOf(u) {
    var m = /go\.routines\.club\/([a-zA-Z0-9_-]+)/.exec(u) ||
            /routines\.club\/go\/([a-zA-Z0-9_-]+)/.exec(u);
    return m ? m[1] : "";
  }

  function networkOf(u) {
    if (/lvnta\.com/.test(u)) return "levanta";
    if (/amazon\.|amzn\.to|a\.co\//.test(u)) return "amazon";
    if (/\.sjv\.io|\.pxf\.io/.test(u)) return "impact";
    if (/jdoqocy|kqzyfj|tkqlhce|anrdoezrs/.test(u)) return "cj";
    if (/visionbody/.test(u)) return "brand";
    if (/go\.routines\.club|routines\.club\/go\//.test(u)) return "go";
    return "";
  }

  function placementOf(a) {
    if (a.closest(".lab-rail")) return "rail";
    if (a.closest(".lab-content")) return "body";
    if (a.closest("#stack")) return "module";
    if (a.closest("footer") || a.closest(".lab-ems-band")) return "footer";
    return "other";
  }

  /* ---- 1. attribution stopgap ------------------------------------------- */
  function stampHandle() {
    var h = HANDLE;
    if (!h) return;
    var links = document.querySelectorAll('a[href*="go.routines.club"], a[href*="routines.club/go/"]');
    Array.prototype.forEach.call(links, function (a) {
      var href = a.getAttribute("href") || "";
      if (MAAS.test(href)) return;                 // signed Amazon link, never touch
      var u;
      try { u = new URL(href, location.href); } catch (e) { return; }
      if (u.searchParams.get("a")) return;         // already attributed
      u.searchParams.set("a", h);
      a.setAttribute("href", u.toString());
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", stampHandle);
  } else {
    stampHandle();
  }

  /* ---- 2 + 3. click tracking -------------------------------------------- */
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;
    var href = a.href || "";

    if (a.hasAttribute("data-promo")) {
      send("ems_promo_click", {
        placement: a.getAttribute("data-promo-placement") || "",
        variant: a.getAttribute("data-promo-variant") || "",
        promo_from: a.getAttribute("data-promo-from") || "",
        link_url: href
      });
      return;
    }

    var sponsored = (a.rel || "").indexOf("sponsored") !== -1;
    if (!AFFILIATE.test(href) && !sponsored) return;

    send("affiliate_click", {
      link_url: href,
      link_domain: hostOf(href),
      network: networkOf(href),
      product: slugOf(href) || (a.textContent || "").trim().slice(0, 80),
      placement: placementOf(a)
    });
  }, true);

  /* ---- 4. read depth ----------------------------------------------------- */
  /*
   * Computed from scroll position, not IntersectionObserver sentinels.
   *
   * The sentinel approach was tried first and silently under-reported: IO gives no
   * guarantee it will fire for a 1px element that passes entirely through the
   * viewport between two frames, which is precisely what a fast flick-scroll on a
   * phone does. Verified in-browser: all four sentinels scrolled past, zero events.
   * Since the whole point of this metric is diagnosing MOBILE drop-off, a method
   * that loses fast mobile scrollers would have been worse than useless -- it would
   * have looked like data.
   *
   * Measuring how far down .lab-content the viewport BOTTOM has reached is also the
   * more honest definition: it counts content actually revealed, and it cannot miss
   * a milestone, because depth is monotonic and re-checked on every scroll tick.
   */
  (function () {
    var content = document.querySelector(".lab-content");
    if (!content) return;

    var MARKS = [25, 50, 75, 90];
    var fired = {};
    var ticking = false;

    function check() {
      ticking = false;
      var rect = content.getBoundingClientRect();
      var top = rect.top + window.pageYOffset;
      var height = content.offsetHeight;
      if (height <= 0) return;

      // How far the bottom of the viewport has travelled into the article.
      var seen = (window.pageYOffset + window.innerHeight) - top;
      var pct = Math.max(0, Math.min(100, (seen / height) * 100));

      for (var i = 0; i < MARKS.length; i++) {
        var m = MARKS[i];
        if (pct >= m && !fired[m]) {
          fired[m] = true;
          send("read_depth", { percent: m });
        }
      }
      if (fired[90]) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Short articles can already satisfy a milestone at load, with no scroll to come.
    check();
  })();
})();
